import { Controller } from "@hotwired/stimulus"

/**
 * ApplicationFormController - Manages the multi-step tenant application wizard
 *
 * Steps:
 *   1. About You - Lead applicant details
 *   2. Household - Other adults, children, pets
 *   3. Timing - Start date, guarantor, message
 *   4. Review - Summary and declarations
 *
 * Features:
 *   - Smooth step transitions
 *   - Form validation per step
 *   - Progress indicator updates
 *   - Responsive design (desktop/mobile)
 */
export default class extends Controller {
  static targets = [
    // Welcome step (pre-form)
    "welcomeStep",
    "welcomeCheckbox",
    "welcomeContinueButton",
    // Form container (hidden during welcome)
    "formContainer",
    // Step containers
    "step1",
    "step2",
    "step3",
    "step4",
    // Progress indicators (desktop sidebar)
    "progressStep1",
    "progressStep1Circle",
    "progressStep1Text",
    "progressStep2",
    "progressStep3",
    "progressStep4",
    // Step indicator breadcrumbs
    "indicatorStep1",
    "indicatorStep2",
    "indicatorStep3",
    "indicatorStep4",
    // Mobile navigation
    "mobileBar",
    "mobileBackButton",
    "mobileNextButton",
    "mobileDot1",
    "mobileDot2",
    "mobileDot3",
    "mobileDot4",
    // Form and inputs
    "form",
    "stepError",
    // Lead applicant inputs
    "leadFirstName",
    "leadLastName",
    "leadDateOfBirth",
    "leadEmail",
    "leadPhone",
    "leadEmployment",
    "leadJobTitle",
    "leadIncome",
    "leadNationality",
    "leadAdverseCreditDetails",
    // Timing inputs
    "startDate",
    "proposedRent",
    "rentWarning",
    "rentOverWarning",
    // Step continue buttons (for enabling/disabling)
    "step1Continue",
    "step2Continue",
    "step3Continue",
    // Review summary - Lead applicant
    "summaryName",
    "summaryEmail",
    "summaryPhone",
    "summaryEmployment",
    "summaryIncome",
    // Review summary - Household
    "summaryAdults",
    "summaryChildren",
    "summaryPets",
    // Review summary - Timing
    "summaryRent",
    "summaryDate",
    "summaryGuarantorRow",
    "summaryGuarantorRowConditional",
    "summaryGuarantor",
    "summaryMessageSection",
    "summaryMessage",
    // Declarations (single terms acceptance checkbox)
    "declaration1",
    // Submit button
    "submitButton",
    // Conditional guarantor section
    "conditionalGuarantorSection",
    // Guarantor details — required once the applicant says a guarantor is available
    "guarantorDetails",
    "guarantorDeclinedWarning",
    "guarantorFirstName",
    "guarantorLastName",
    "guarantorDob",
    "guarantorRelationship",
    "guarantorRelationshipDetails",
    "guarantorRelationshipDetailsInput",
    "guarantorPhone",
    "guarantorEmail",
    "guarantorAddressLine1",
    "guarantorTown",
    "guarantorPostcode",
    "guarantorOccupation",
    "guarantorIncome",
    "summaryGuarantorSection",
    "summaryGuarantorFullName",
    "summaryGuarantorDob",
    "summaryGuarantorRelationship",
    "summaryGuarantorEmail",
    "summaryGuarantorPhone",
    "summaryGuarantorAddress",
    "summaryGuarantorOccupation",
    "summaryGuarantorIncome",
    // Other applicants summary in review step
    "otherApplicantsSummary"
  ]

  static values = {
    currentStep: { type: Number, default: 1 },
    totalSteps: { type: Number, default: 4 },
    askNationality: { type: Boolean, default: false },
    guarantorMode: { type: String, default: "always" },
    requiredIncome: { type: Number, default: 0 },
    welcomeConfirmed: { type: Boolean, default: false }
  }

  connect() {
    // Show welcome step initially if present and not confirmed
    if (this.hasWelcomeStepTarget && !this.welcomeConfirmedValue) {
      this.showWelcomeStep()
    } else {
      this.showFormSteps()
    }
    this.setupFormListeners()
    this.toggleGuarantorDetails()
    this.toggleGuarantorRelationshipDetails()
    this.updateContinueButtonState()
    this.updateWelcomeContinueButton()
  }

  // Show the welcome step (pre-form page)
  showWelcomeStep() {
    if (this.hasWelcomeStepTarget) {
      this.welcomeStepTarget.classList.remove("hidden")
    }
    if (this.hasFormContainerTarget) {
      this.formContainerTarget.classList.add("hidden")
    }
  }

  // Show the form steps (after welcome is confirmed)
  showFormSteps() {
    if (this.hasWelcomeStepTarget) {
      this.welcomeStepTarget.classList.add("hidden")
    }
    if (this.hasFormContainerTarget) {
      this.formContainerTarget.classList.remove("hidden")
    }
    this.showStep(this.currentStepValue)
    this.updateMobileBar()
    this.updateProgressIndicators()
  }

  // Handle welcome checkbox change
  welcomeCheckboxChanged() {
    this.updateWelcomeContinueButton()
  }

  // Update the welcome continue button state
  updateWelcomeContinueButton() {
    if (this.hasWelcomeContinueButtonTarget && this.hasWelcomeCheckboxTarget) {
      this.welcomeContinueButtonTarget.disabled = !this.welcomeCheckboxTarget.checked
    }
  }

  // Confirm welcome and proceed to form
  confirmWelcome(event) {
    if (event) event.preventDefault()

    if (this.hasWelcomeCheckboxTarget && this.welcomeCheckboxTarget.checked) {
      this.welcomeConfirmedValue = true
      this.showFormSteps()
      // Scroll the overflow container to top (not window, as body has overflow-hidden)
      const scrollContainer = document.querySelector('.overflow-auto')
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: 0, behavior: "smooth" })
      }
    }
  }

  // Set up listeners on form inputs to update button state in real-time
  setupFormListeners() {
    // Listen for input/change events on all form fields
    this.element.addEventListener("input", () => this.updateContinueButtonState())
    this.element.addEventListener("change", () => this.updateContinueButtonState())
  }

  // Update the continue button enabled/disabled state based on current step validity
  updateContinueButtonState() {
    let isValid = false

    switch (this.currentStepValue) {
      case 1:
        isValid = this.isStep1Valid()
        break
      case 2:
        isValid = this.isStep2Valid()
        break
      case 3:
        isValid = this.isStep3Valid()
        break
      case 4:
        isValid = this.isStep4Valid()
        break
      default:
        isValid = true
    }

    // Update desktop continue button for current step
    const stepContinueTarget = this[`step${this.currentStepValue}ContinueTarget`]
    const hasStepContinueTarget = this[`hasStep${this.currentStepValue}ContinueTarget`]

    if (hasStepContinueTarget && stepContinueTarget) {
      this.setButtonEnabled(stepContinueTarget, isValid)
    }

    // Update ALL mobile continue buttons (there may be multiple)
    if (this.hasMobileNextButtonTarget) {
      this.mobileNextButtonTargets.forEach(button => {
        this.setButtonEnabled(button, isValid)
      })
    }

    // Update submit button on step 4
    if (this.currentStepValue === 4 && this.hasSubmitButtonTarget) {
      this.setButtonEnabled(this.submitButtonTarget, isValid)
    }
  }

  // Helper to enable/disable a button (styling handled by CSS disabled: variants)
  setButtonEnabled(button, enabled) {
    button.disabled = !enabled
  }

  // Silent validation for Step 1 (returns true/false without showing errors)
  isStep1Valid() {
    if (this.hasLeadFirstNameTarget && !this.leadFirstNameTarget.value.trim()) return false
    if (this.hasLeadLastNameTarget && !this.leadLastNameTarget.value.trim()) return false
    if (this.hasLeadEmailTarget && !this.leadEmailTarget.value.trim()) return false
    if (this.hasLeadEmailTarget && !this.isValidEmail(this.leadEmailTarget.value)) return false
    if (this.hasLeadPhoneTarget && !this.leadPhoneTarget.value.trim()) return false
    if (this.hasLeadPhoneTarget && !this.isValidUKPhone(this.leadPhoneTarget.value)) return false
    if (this.hasLeadEmploymentTarget && !this.leadEmploymentTarget.value) return false
    // Job title required for employed/self-employed
    const employmentStatus = this.hasLeadEmploymentTarget ? this.leadEmploymentTarget.value : ""
    if ((employmentStatus === "employed" || employmentStatus === "self_employed") &&
        this.hasLeadJobTitleTarget && !this.leadJobTitleTarget.value.trim()) return false
    if (this.hasLeadIncomeTarget && !this.leadIncomeTarget.value) return false

    // Nationality (if enabled)
    if (this.askNationalityValue && this.hasLeadNationalityTarget && !this.leadNationalityTarget.value.trim()) return false

    // Adverse credit details (required if adverse credit is yes)
    const adverseCredit = this.element.querySelector('input[name="applicant_1[adverse_credit]"]:checked')
    if (adverseCredit && adverseCredit.value === "1") {
      if (this.hasLeadAdverseCreditDetailsTarget && !this.leadAdverseCreditDetailsTarget.value.trim()) return false
    }

    // Right to rent
    const rightToRent = this.element.querySelector('input[name="applicant_1[right_to_rent]"]:checked')
    if (!rightToRent) return false
    if (rightToRent.value === "no") return false

    return true
  }

  // Silent validation for Step 2
  isStep2Valid() {
    // Pets question is required
    const hasPets = this.element.querySelector('input[name="household[has_pets]"]:checked')
    if (!hasPets) return false

    // Validate each other adult that has been started (has a first name)
    const firstNameInputs = this.element.querySelectorAll('input[name^="other_adults["][name$="[first_name]"]')

    const leadEmail = this.hasLeadEmailTarget ? this.leadEmailTarget.value.trim().toLowerCase() : ""
    const leadPhone = this.hasLeadPhoneTarget ? this.normalizePhone(this.leadPhoneTarget.value) : ""

    const seenEmails = []
    const seenPhones = []

    for (let i = 0; i < firstNameInputs.length; i++) {
      const firstName = firstNameInputs[i].value.trim()
      if (!firstName) continue // Skip empty entries

      // Helper to get field value for this adult
      const getFieldValue = (fieldName) => {
        const el = this.element.querySelector(`[name="other_adults[${i}][${fieldName}]"]`)
        return el?.value?.trim() || ""
      }
      const getRadioValue = (fieldName) => {
        const el = this.element.querySelector(`[name="other_adults[${i}][${fieldName}]"]:checked`)
        return el?.value || ""
      }

      // Validate required fields for this adult
      if (!getFieldValue("last_name")) return false
      if (!getFieldValue("email")) return false
      if (!this.isValidEmail(getFieldValue("email"))) return false
      if (!getFieldValue("phone")) return false
      if (!this.isValidUKPhone(getFieldValue("phone"))) return false
      if (!getFieldValue("employment_status")) return false
      if (!getFieldValue("annual_income")) return false
      if (!getRadioValue("adverse_credit")) return false
      if (!getRadioValue("right_to_rent")) return false
      if (getRadioValue("right_to_rent") === "no") return false

      // Job title required for employed/self-employed
      const employmentStatus = getFieldValue("employment_status")
      if ((employmentStatus === "employed" || employmentStatus === "self_employed") &&
          !getFieldValue("job_title")) return false

      // Nationality required if setting enabled
      if (this.askNationalityValue && !getFieldValue("nationality")) return false

      // Check for duplicate email
      const email = getFieldValue("email").toLowerCase()
      if (email === leadEmail) return false
      if (seenEmails.includes(email)) return false
      seenEmails.push(email)

      // Check for duplicate phone
      const phone = this.normalizePhone(getFieldValue("phone"))
      if (phone === leadPhone) return false
      if (seenPhones.includes(phone)) return false
      seenPhones.push(phone)
    }

    return true
  }

  // Silent validation for Step 3
  isStep3Valid() {
    if (this.hasStartDateTarget && !this.startDateTarget.value.trim()) return false
    if (this.hasProposedRentTarget && !this.isProposedRentValid()) return false
    if (this.guarantorQuestionVisible() && !this.guarantorAnswer()) return false
    if (this.guarantorDetailsRequired() && this.guarantorDetailsErrors().length > 0) return false
    return true
  }

  // Offered rent must be a positive figure, no greater than the advertised price
  isProposedRentValid() {
    const advertised = parseFloat(this.proposedRentTarget.dataset.advertisedRent)
    const value = parseFloat(this.proposedRentTarget.value)
    if (isNaN(value) || value <= 0) return false
    if (!isNaN(advertised) && value > advertised) return false
    return true
  }

  // Toggle the below/above advertised price notices as the applicant types.
  // The above-price notice matters: Continue is disabled for an over-asking
  // offer, so without it the applicant gets a dead button and no reason why.
  updateRentWarning() {
    if (!this.hasProposedRentTarget) return
    const advertised = parseFloat(this.proposedRentTarget.dataset.advertisedRent)
    const value = parseFloat(this.proposedRentTarget.value)
    const entered = !isNaN(value) && !isNaN(advertised) && value > 0

    if (this.hasRentWarningTarget) {
      this.rentWarningTarget.classList.toggle("hidden", !(entered && value < advertised))
    }
    if (this.hasRentOverWarningTarget) {
      this.rentOverWarningTarget.classList.toggle("hidden", !(entered && value > advertised))
    }
  }

  // Silent validation for Step 4
  isStep4Valid() {
    if (this.hasDeclaration1Target && !this.declaration1Target.checked) return false
    return true
  }

  // Navigation methods
  nextStep(event) {
    if (event) event.preventDefault()

    // Validate current step
    if (!this.validateCurrentStep()) {
      return
    }

    // Clear any previous errors
    this.hideError()

    // Move to next step
    if (this.currentStepValue < this.totalStepsValue) {
      this.currentStepValue++
      this.showStep(this.currentStepValue)
      this.updateMobileBar()
      this.updateProgressIndicators()
      this.updateSummary()
      this.updateConditionalGuarantor()

      // Scroll to top of form
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  previousStep(event) {
    if (event) event.preventDefault()

    // Clear any errors
    this.hideError()

    if (this.currentStepValue > 1) {
      this.currentStepValue--
      this.showStep(this.currentStepValue)
      this.updateMobileBar()
      this.updateProgressIndicators()

      // Scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  goToStep(event) {
    if (event) event.preventDefault()

    const step = parseInt(event.currentTarget.dataset.step || event.params?.step)
    if (step && step >= 1 && step <= this.totalStepsValue) {
      // Can only go back, or go forward if current step is valid
      if (step < this.currentStepValue || this.validateCurrentStep()) {
        this.hideError()
        this.currentStepValue = step
        this.showStep(step)
        this.updateMobileBar()
        this.updateProgressIndicators()
        window.scrollTo({ top: 0, behavior: "smooth" })
      }
    }
  }

  // Display methods
  showStep(step) {
    // Hide all steps
    this.step1Target.classList.add("hidden")
    this.step2Target.classList.add("hidden")
    this.step3Target.classList.add("hidden")
    this.step4Target.classList.add("hidden")

    // Show current step with animation
    const stepTarget = this[`step${step}Target`]
    if (stepTarget) {
      stepTarget.classList.remove("hidden")
      stepTarget.classList.add("animate-fade-in")
    }

    // Update button state for the new step
    this.updateContinueButtonState()
  }

  updateMobileBar() {
    // Update back button visibility
    if (this.hasMobileBackButtonTarget) {
      if (this.currentStepValue > 1) {
        this.mobileBackButtonTarget.classList.remove("hidden")
      } else {
        this.mobileBackButtonTarget.classList.add("hidden")
      }
    }

    // Update next/submit button text on ALL mobile next buttons
    // (There may be multiple if welcome step has its own mobile bar)
    if (this.hasMobileNextButtonTarget) {
      const buttons = this.mobileNextButtonTargets
      buttons.forEach(button => {
        if (this.currentStepValue === this.totalStepsValue) {
          button.textContent = "Submit Application"
          // Keep black styling for neutral multi-tenant appearance
          button.classList.add("bg-gray-900", "hover:bg-gray-800")
          button.classList.remove("bg-green-600", "hover:bg-green-700")
          // Change action to submit
          button.dataset.action = "click->application-form#submit"
        } else {
          button.textContent = "Continue"
          button.classList.add("bg-gray-900", "hover:bg-gray-800")
          button.classList.remove("bg-green-600", "hover:bg-green-700")
          button.dataset.action = "click->application-form#nextStep"
        }
      })
    }

    // Update dots
    const dots = [
      this.hasMobileDot1Target ? this.mobileDot1Target : null,
      this.hasMobileDot2Target ? this.mobileDot2Target : null,
      this.hasMobileDot3Target ? this.mobileDot3Target : null,
      this.hasMobileDot4Target ? this.mobileDot4Target : null
    ]

    dots.forEach((dot, index) => {
      if (dot) {
        if (index + 1 === this.currentStepValue) {
          dot.classList.remove("bg-gray-300")
          dot.classList.add("bg-gray-900")
        } else {
          dot.classList.add("bg-gray-300")
          dot.classList.remove("bg-gray-900")
        }
      }
    })
  }

  updateProgressIndicators() {
    // Update sidebar progress (desktop)
    for (let i = 1; i <= this.totalStepsValue; i++) {
      const progressTarget = this[`progressStep${i}Target`]
      const indicatorTarget = this[`indicatorStep${i}Target`]

      if (progressTarget) {
        const circle = progressTarget.querySelector("div")
        const text = progressTarget.querySelector("span")

        if (i < this.currentStepValue) {
          // Completed step - show checkmark, clickable
          circle.classList.remove("bg-gray-200", "text-gray-500")
          circle.classList.add("bg-gray-900", "text-white")
          circle.innerHTML = `<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path></svg>`
          text.classList.remove("text-gray-500")
          text.classList.add("text-gray-900")
          // Make clickable
          progressTarget.classList.add("cursor-pointer")
          progressTarget.classList.remove("cursor-not-allowed", "opacity-50")
        } else if (i === this.currentStepValue) {
          // Current step - show number, clickable
          circle.classList.remove("bg-gray-200", "text-gray-500")
          circle.classList.add("bg-gray-900", "text-white")
          circle.innerHTML = i
          text.classList.remove("text-gray-500")
          text.classList.add("text-gray-900")
          // Make clickable
          progressTarget.classList.add("cursor-pointer")
          progressTarget.classList.remove("cursor-not-allowed", "opacity-50")
        } else {
          // Future step - show number, not clickable
          circle.classList.add("bg-gray-200", "text-gray-500")
          circle.classList.remove("bg-gray-900", "text-white")
          circle.innerHTML = i
          text.classList.add("text-gray-500")
          text.classList.remove("text-gray-900")
          // Make not clickable (visual only - JS still prevents navigation)
          progressTarget.classList.remove("cursor-pointer")
          progressTarget.classList.add("cursor-not-allowed", "opacity-50")
        }
      }

      if (indicatorTarget) {
        if (i === this.currentStepValue) {
          indicatorTarget.classList.add("text-gray-900", "font-medium")
          indicatorTarget.classList.remove("text-gray-400")
        } else {
          indicatorTarget.classList.remove("text-gray-900", "font-medium")
          indicatorTarget.classList.add("text-gray-400")
        }
      }
    }
  }

  updateSummary() {
    // Update review summary with form data

    // Lead applicant details
    if (this.hasSummaryNameTarget && this.hasLeadFirstNameTarget && this.hasLeadLastNameTarget) {
      const firstName = this.leadFirstNameTarget.value.trim()
      const lastName = this.leadLastNameTarget.value.trim()
      this.summaryNameTarget.textContent = firstName && lastName ? `${firstName} ${lastName}` : "-"
    }
    if (this.hasSummaryEmailTarget && this.hasLeadEmailTarget) {
      this.summaryEmailTarget.textContent = this.leadEmailTarget.value || "-"
    }
    if (this.hasSummaryPhoneTarget && this.hasLeadPhoneTarget) {
      this.summaryPhoneTarget.textContent = this.leadPhoneTarget.value || "-"
    }
    if (this.hasSummaryEmploymentTarget && this.hasLeadEmploymentTarget) {
      const employment = this.leadEmploymentTarget.value
      const employmentLabels = {
        "employed": "Employed",
        "self_employed": "Self-Employed",
        "student": "Student",
        "retired": "Retired",
        "not_working": "Not Currently Working",
        "other": "Other"
      }
      let employmentDisplay = employmentLabels[employment] || "-"
      // Add job title for employed/self-employed
      if ((employment === "employed" || employment === "self_employed") && this.hasLeadJobTitleTarget) {
        const jobTitle = this.leadJobTitleTarget.value.trim()
        if (jobTitle) {
          employmentDisplay = `${employmentDisplay} - ${jobTitle}`
        }
      }
      this.summaryEmploymentTarget.textContent = employmentDisplay
    }
    if (this.hasSummaryIncomeTarget && this.hasLeadIncomeTarget) {
      const income = this.leadIncomeTarget.value
      if (income) {
        this.summaryIncomeTarget.textContent = `£${parseInt(income).toLocaleString()}`
      } else {
        this.summaryIncomeTarget.textContent = "-"
      }
    }

    // Other applicants summary
    this.updateOtherApplicantsSummary()

    // Household details
    if (this.hasSummaryAdultsTarget) {
      // Count other adults from Alpine.js state via DOM
      const otherAdultInputs = this.element.querySelectorAll('input[name^="other_adults["][name$="[first_name]"]')
      const filledAdults = Array.from(otherAdultInputs).filter(input => input.value.trim() !== "").length
      const totalAdults = 1 + filledAdults
      this.summaryAdultsTarget.textContent = totalAdults === 1 ? "1 adult" : `${totalAdults} adults`
    }
    if (this.hasSummaryChildrenTarget) {
      const childrenSelect = this.element.querySelector('select[name="household[children_count]"]')
      if (childrenSelect) {
        const count = parseInt(childrenSelect.value) || 0
        if (count === 0) {
          this.summaryChildrenTarget.textContent = "None"
        } else if (count === 1) {
          this.summaryChildrenTarget.textContent = "1 child"
        } else if (count === 5) {
          this.summaryChildrenTarget.textContent = "5+ children"
        } else {
          this.summaryChildrenTarget.textContent = `${count} children`
        }
      }
    }
    if (this.hasSummaryPetsTarget) {
      const hasPets = this.element.querySelector('input[name="household[has_pets]"]:checked')
      const petDetails = this.element.querySelector('textarea[name="household[pet_details]"]')
      if (hasPets && hasPets.value === "1") {
        const details = petDetails?.value?.trim()
        this.summaryPetsTarget.textContent = details || "Yes"
      } else {
        this.summaryPetsTarget.textContent = "None"
      }
    }

    // Offered rent
    if (this.hasSummaryRentTarget && this.hasProposedRentTarget) {
      const rent = parseFloat(this.proposedRentTarget.value)
      this.summaryRentTarget.textContent = isNaN(rent) || rent <= 0
        ? "-"
        : `£${rent.toLocaleString()} per month`
    }

    // Timing details
    if (this.hasSummaryDateTarget && this.hasStartDateTarget) {
      const dateValue = this.startDateTarget.value
      if (dateValue) {
        const date = new Date(dateValue)
        this.summaryDateTarget.textContent = date.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric"
        })
      } else {
        this.summaryDateTarget.textContent = "-"
      }
    }

    // Guarantor
    if (this.hasSummaryGuarantorTarget) {
      const guarantor = this.element.querySelector('input[name="guarantor_available"]:checked')
      if (guarantor) {
        const labels = { "yes": "Yes", "no": "No" }
        this.summaryGuarantorTarget.textContent = labels[guarantor.value] || guarantor.value
      } else {
        this.summaryGuarantorTarget.textContent = "-"
      }
    }

    // The guarantor, in full, once their details have been given
    this.updateGuarantorSummary()

    // Show/hide conditional guarantor row based on whether the question was shown
    if (this.hasSummaryGuarantorRowConditionalTarget) {
      const guarantorNeeded = this.calculateGuarantorNeeded()
      const guarantorSelected = this.element.querySelector('input[name="guarantor_available"]:checked')

      if (guarantorNeeded && guarantorSelected) {
        this.summaryGuarantorRowConditionalTarget.classList.remove("hidden")
      } else {
        this.summaryGuarantorRowConditionalTarget.classList.add("hidden")
      }
    }

    // Message to landlord
    if (this.hasSummaryMessageTarget && this.hasSummaryMessageSectionTarget) {
      const messageTextarea = this.element.querySelector('textarea[name="message_to_landlord"]')
      const message = messageTextarea?.value?.trim()
      if (message) {
        this.summaryMessageTarget.textContent = message
        this.summaryMessageSectionTarget.classList.remove("hidden")
      } else {
        this.summaryMessageSectionTarget.classList.add("hidden")
      }
    }
  }

  /**
   * Update the other applicants summary section in the review step
   * Reads data from other_adults form fields and generates HTML for each
   */
  updateOtherApplicantsSummary() {
    if (!this.hasOtherApplicantsSummaryTarget) return

    // Get all other adult first name inputs to count filled entries
    const firstNameInputs = this.element.querySelectorAll('input[name^="other_adults["][name$="[first_name]"]')
    const filledAdults = []

    firstNameInputs.forEach((input, index) => {
      const firstName = input.value.trim()
      if (firstName) {
        // Get all fields for this adult
        const getFieldValue = (fieldName) => {
          const el = this.element.querySelector(`[name="other_adults[${index}][${fieldName}]"]`)
          return el?.value?.trim() || ""
        }

        filledAdults.push({
          index: index + 2, // Applicant 2, 3, etc.
          firstName: firstName,
          lastName: getFieldValue("last_name"),
          email: getFieldValue("email"),
          phone: getFieldValue("phone"),
          employmentStatus: getFieldValue("employment_status"),
          jobTitle: getFieldValue("job_title"),
          annualIncome: getFieldValue("annual_income")
        })
      }
    })

    // Generate HTML for each other applicant
    if (filledAdults.length === 0) {
      this.otherApplicantsSummaryTarget.innerHTML = ""
      return
    }

    const employmentLabels = {
      "employed": "Employed",
      "self_employed": "Self-Employed",
      "student": "Student",
      "retired": "Retired",
      "not_working": "Not Currently Working",
      "other": "Other"
    }

    let html = ""
    filledAdults.forEach(adult => {
      let employmentDisplay = employmentLabels[adult.employmentStatus] || "-"
      if ((adult.employmentStatus === "employed" || adult.employmentStatus === "self_employed") && adult.jobTitle) {
        employmentDisplay = `${employmentDisplay} - ${adult.jobTitle}`
      }

      const incomeDisplay = adult.annualIncome ? `£${parseInt(adult.annualIncome).toLocaleString()}` : "-"
      const fullName = `${adult.firstName} ${adult.lastName}`.trim() || "-"

      html += `
        <div class="pt-3 mt-3 border-t border-gray-200 space-y-2">
          <div class="flex items-center justify-between">
            <h4 class="text-xs font-medium text-gray-500 uppercase tracking-wide">Applicant ${adult.index}</h4>
            <button type="button"
                    class="text-xs text-gray-500 hover:text-gray-900 underline"
                    data-action="click->application-form#goToStep"
                    data-step="2">
              Edit
            </button>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-gray-500">Name</span>
            <span class="text-gray-900 text-right">${this.escapeHtml(fullName)}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-gray-500">Email</span>
            <span class="text-gray-900 text-right">${this.escapeHtml(adult.email || "-")}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-gray-500">Phone</span>
            <span class="text-gray-900 text-right">${this.escapeHtml(adult.phone || "-")}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-gray-500">Employment</span>
            <span class="text-gray-900 text-right">${this.escapeHtml(employmentDisplay)}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-gray-500">Annual Income</span>
            <span class="text-gray-900 text-right">${incomeDisplay}</span>
          </div>
        </div>
      `
    })

    this.otherApplicantsSummaryTarget.innerHTML = html
  }

  /**
   * Escape HTML special characters to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  // Validation methods
  validateCurrentStep() {
    switch (this.currentStepValue) {
      case 1:
        return this.validateStep1()
      case 2:
        return this.validateStep2()
      case 3:
        return this.validateStep3()
      case 4:
        return this.validateStep4()
      default:
        return true
    }
  }

  validateStep1() {
    // Lead applicant required fields
    const errors = []

    if (this.hasLeadFirstNameTarget && !this.leadFirstNameTarget.value.trim()) {
      errors.push("First name is required")
    }
    if (this.hasLeadLastNameTarget && !this.leadLastNameTarget.value.trim()) {
      errors.push("Last name is required")
    }
    if (this.hasLeadEmailTarget && !this.leadEmailTarget.value.trim()) {
      errors.push("Email address is required")
    } else if (this.hasLeadEmailTarget && !this.isValidEmail(this.leadEmailTarget.value)) {
      errors.push("Please enter a valid email address")
    }
    if (this.hasLeadPhoneTarget && !this.leadPhoneTarget.value.trim()) {
      errors.push("Phone number is required")
    } else if (this.hasLeadPhoneTarget && !this.isValidUKPhone(this.leadPhoneTarget.value)) {
      errors.push("Please enter a valid UK phone number")
    }
    if (this.hasLeadEmploymentTarget && !this.leadEmploymentTarget.value) {
      errors.push("Employment status is required")
    }
    // Job title validation (required if employed or self-employed)
    const employmentStatus = this.hasLeadEmploymentTarget ? this.leadEmploymentTarget.value : ""
    if ((employmentStatus === "employed" || employmentStatus === "self_employed") &&
        this.hasLeadJobTitleTarget && !this.leadJobTitleTarget.value.trim()) {
      errors.push("Job title is required")
    }
    if (this.hasLeadIncomeTarget && !this.leadIncomeTarget.value) {
      errors.push("Annual income is required")
    }

    // Nationality validation (only if the field is shown, i.e., askNationality is true)
    if (this.askNationalityValue && this.hasLeadNationalityTarget && !this.leadNationalityTarget.value.trim()) {
      errors.push("Nationality is required")
    }

    // Adverse credit details validation (required if adverse credit is yes)
    const adverseCredit = this.element.querySelector('input[name="applicant_1[adverse_credit]"]:checked')
    if (adverseCredit && adverseCredit.value === "1") {
      if (this.hasLeadAdverseCreditDetailsTarget && !this.leadAdverseCreditDetailsTarget.value.trim()) {
        errors.push("Please provide details about your credit history")
      }
    }

    // Right to rent validation
    const rightToRent = this.element.querySelector('input[name="applicant_1[right_to_rent]"]:checked')
    if (!rightToRent) {
      errors.push("Right to rent status is required")
    } else if (rightToRent.value === "no") {
      errors.push("Unfortunately, we cannot accept applications from individuals who do not have the right to rent in the UK")
    }

    if (errors.length > 0) {
      this.showError(errors.join(". "))
      return false
    }
    return true
  }

  validateStep2() {
    const errors = []

    // Pets question is required
    const hasPets = this.element.querySelector('input[name="household[has_pets]"]:checked')
    if (!hasPets) {
      errors.push("Please indicate whether any pets will live at the property")
    }

    // Collect lead applicant's email and phone
    const leadEmail = this.hasLeadEmailTarget ? this.leadEmailTarget.value.trim().toLowerCase() : ""
    const leadPhone = this.hasLeadPhoneTarget ? this.normalizePhone(this.leadPhoneTarget.value) : ""

    // Collect all other adults' details and validate
    const firstNameInputs = this.element.querySelectorAll('input[name^="other_adults["][name$="[first_name]"]')

    const seenEmails = []
    const seenPhones = []

    for (let i = 0; i < firstNameInputs.length; i++) {
      const firstName = firstNameInputs[i].value.trim()
      if (!firstName) continue // Skip empty entries

      const applicantNum = i + 2 // Applicant 2, 3, etc.

      // Helper to get field value for this adult
      const getFieldValue = (fieldName) => {
        const el = this.element.querySelector(`[name="other_adults[${i}][${fieldName}]"]`)
        return el?.value?.trim() || ""
      }
      const getRadioValue = (fieldName) => {
        const el = this.element.querySelector(`[name="other_adults[${i}][${fieldName}]"]:checked`)
        return el?.value || ""
      }

      // Validate required fields
      if (!getFieldValue("last_name")) {
        errors.push(`Applicant ${applicantNum}: Last name is required`)
      }
      if (!getFieldValue("email")) {
        errors.push(`Applicant ${applicantNum}: Email address is required`)
      } else if (!this.isValidEmail(getFieldValue("email"))) {
        errors.push(`Applicant ${applicantNum}: Please enter a valid email address`)
      }
      if (!getFieldValue("phone")) {
        errors.push(`Applicant ${applicantNum}: Phone number is required`)
      } else if (!this.isValidUKPhone(getFieldValue("phone"))) {
        errors.push(`Applicant ${applicantNum}: Please enter a valid UK phone number`)
      }
      if (!getFieldValue("employment_status")) {
        errors.push(`Applicant ${applicantNum}: Employment status is required`)
      }
      if (!getFieldValue("annual_income")) {
        errors.push(`Applicant ${applicantNum}: Annual income is required`)
      }
      if (!getRadioValue("adverse_credit")) {
        errors.push(`Applicant ${applicantNum}: Please indicate if they have adverse credit history`)
      }
      if (!getRadioValue("right_to_rent")) {
        errors.push(`Applicant ${applicantNum}: Right to rent status is required`)
      } else if (getRadioValue("right_to_rent") === "no") {
        errors.push(`Applicant ${applicantNum}: Unfortunately, we cannot accept applications from individuals who do not have the right to rent in the UK`)
      }

      // Job title required for employed/self-employed
      const employmentStatus = getFieldValue("employment_status")
      if ((employmentStatus === "employed" || employmentStatus === "self_employed") &&
          !getFieldValue("job_title")) {
        errors.push(`Applicant ${applicantNum}: Job title is required`)
      }

      // Nationality required if setting enabled
      if (this.askNationalityValue && !getFieldValue("nationality")) {
        errors.push(`Applicant ${applicantNum}: Nationality is required`)
      }

      // Check for duplicate email
      const email = getFieldValue("email").toLowerCase()
      if (email) {
        if (email === leadEmail) {
          errors.push(`Applicant ${applicantNum}: Email address must be different from the lead applicant's email`)
        } else if (seenEmails.includes(email)) {
          errors.push(`Applicant ${applicantNum}: Email address is already used by another applicant`)
        }
        seenEmails.push(email)
      }

      // Check for duplicate phone
      const phone = this.normalizePhone(getFieldValue("phone"))
      if (phone) {
        if (phone === leadPhone) {
          errors.push(`Applicant ${applicantNum}: Phone number must be different from the lead applicant's phone`)
        } else if (seenPhones.includes(phone)) {
          errors.push(`Applicant ${applicantNum}: Phone number is already used by another applicant`)
        }
        seenPhones.push(phone)
      }
    }

    if (errors.length > 0) {
      this.showError(errors.join(". "))
      return false
    }
    return true
  }

  validateStep3() {
    const errors = []

    // Preferred start date is required
    if (this.hasStartDateTarget && !this.startDateTarget.value.trim()) {
      errors.push("Preferred tenancy start date is required")
    }

    // Offered rent is required and cannot exceed the advertised price
    if (this.hasProposedRentTarget) {
      const advertised = parseFloat(this.proposedRentTarget.dataset.advertisedRent)
      const value = parseFloat(this.proposedRentTarget.value)
      if (isNaN(value) || value <= 0) {
        errors.push("Please enter the rent you'd like to offer")
      } else if (!isNaN(advertised) && value > advertised) {
        errors.push(`The rent you offer cannot be more than the advertised price of £${advertised.toLocaleString()} per month`)
      }
    }

    if (this.guarantorQuestionVisible() && !this.guarantorAnswer()) {
      errors.push("Please tell us whether a guarantor would be available")
    }

    if (this.guarantorDetailsRequired()) {
      errors.push(...this.guarantorDetailsErrors())
      this.renderGuarantorFieldErrors(true)
    }

    if (errors.length > 0) {
      this.showError(errors.join(". "))
      return false
    }
    return true
  }

  validateStep4() {
    // Terms acceptance must be checked
    const errors = []

    if (this.hasDeclaration1Target && !this.declaration1Target.checked) {
      errors.push("Please agree to the terms and conditions to submit your application")
    }

    if (errors.length > 0) {
      this.showError(errors.join(". "))
      return false
    }
    return true
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  /**
   * Normalize a phone number by removing all non-digit characters
   * and converting +44 prefix to 0
   */
  normalizePhone(phone) {
    if (!phone) return ""
    // Remove all non-digit characters except leading +
    let normalized = phone.trim()
    // Handle +44 prefix (UK country code)
    if (normalized.startsWith("+44")) {
      normalized = "0" + normalized.slice(3)
    } else if (normalized.startsWith("0044")) {
      normalized = "0" + normalized.slice(4)
    } else if (normalized.startsWith("44") && normalized.length > 10) {
      normalized = "0" + normalized.slice(2)
    }
    // Remove all remaining non-digits
    return normalized.replace(/\D/g, "")
  }

  /**
   * Validate UK phone number format
   * Accepts: 07xxx xxxxxx (mobile), 01xxx xxxxxx, 02x xxxx xxxx, 03xxx xxxxxx (landline)
   * With or without +44 prefix, various spacing allowed
   */
  isValidUKPhone(phone) {
    if (!phone) return false
    const normalized = this.normalizePhone(phone)

    // UK phone numbers should be 10 or 11 digits when normalized
    if (normalized.length < 10 || normalized.length > 11) {
      return false
    }

    // Must start with 0 after normalization
    if (!normalized.startsWith("0")) {
      return false
    }

    // Valid UK prefixes:
    // 07 - Mobile (must be 11 digits)
    // 01 - Geographic landlines
    // 02 - Geographic landlines (London 020, others)
    // 03 - Non-geographic (same cost as landline)
    const validPrefixes = ["01", "02", "03", "07"]
    const prefix = normalized.substring(0, 2)

    if (!validPrefixes.includes(prefix)) {
      return false
    }

    // Mobile numbers (07) must be 11 digits
    if (prefix === "07" && normalized.length !== 11) {
      return false
    }

    return true
  }

  // Error display methods
  showError(message) {
    // Get the error section for the current step
    const currentStepTarget = this[`step${this.currentStepValue}Target`]
    if (currentStepTarget) {
      const errorSection = currentStepTarget.querySelector('[data-application-form-target="stepError"]')
      if (errorSection) {
        errorSection.textContent = message
        errorSection.classList.remove("hidden")
        // Scroll error into view
        errorSection.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }
  }

  hideError() {
    // Hide all error sections (in case we navigate between steps)
    this.stepErrorTargets.forEach(errorSection => {
      errorSection.classList.add("hidden")
      errorSection.textContent = ""
    })
  }

  // Form submission
  submit(event) {
    if (event) event.preventDefault()

    // A second tap while the first submission is in flight must not create a
    // second application. The page navigates away once the server responds,
    // but until then every submit control has to be dead.
    if (this.submitting) return

    // Validate final step
    if (!this.validateStep4()) {
      return
    }

    // Update summary one more time
    this.updateSummary()

    // Submit the form
    if (this.hasFormTarget) {
      this.submitting = true

      // Disable every submit control. The mobile bar is rendered twice (once
      // in the welcome step, once in the form), so the singular target would
      // only disable the hidden welcome-step button and leave the visible one
      // live for a second tap.
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = true
        this.submitButtonTarget.textContent = "Submitting..."
      }
      this.mobileNextButtonTargets.forEach(button => {
        button.disabled = true
        button.textContent = "Submitting..."
      })

      this.sendApplication()
    }
  }

  /**
   * Submit in the background rather than as a page navigation, so a rejection
   * from the server returns the applicant to the step at fault with every
   * field still filled in, instead of a freshly rendered, empty form.
   */
  async sendApplication() {
    const form = this.formTarget
    let payload

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: { "Accept": "application/json", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin"
      })
      payload = await response.json()
    } catch (error) {
      this.submissionFailed(["We couldn't send your application just now. Please check your connection and try again."], 4)
      return
    }

    if (payload && payload.success) {
      window.location.assign(payload.redirect_url || window.location.href)
      return
    }

    const errors = (payload && payload.errors && payload.errors.length)
      ? payload.errors
      : ["An error occurred submitting your application. Please try again."]
    this.submissionFailed(errors, payload && payload.error_step)
  }

  // Re-arm the submit controls, go to the step that failed, and say why.
  submissionFailed(errors, step) {
    this.submitting = false

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.textContent = "Submit Application"
    }

    const target = parseInt(step)
    if (target && target >= 1 && target <= this.totalStepsValue) {
      this.currentStepValue = target
      this.showStep(target)
    }
    // Resets the mobile bar's button text ("Continue" / "Submit Application")
    // for whichever step is now showing, and re-enables what should be live.
    this.updateMobileBar()
    this.updateProgressIndicators()
    this.updateContinueButtonState()
    this.showError(errors.join(". "))
  }

  /**
   * Update the conditional guarantor section visibility
   * Only relevant when guarantor mode is "conditional"
   * Shows the section if:
   * - Total income is less than required income threshold
   * - OR any applicant has adverse credit
   */
  updateConditionalGuarantor() {
    // Only applies to conditional mode
    if (this.guarantorModeValue !== "conditional") {
      return
    }

    // Find the conditional guarantor section
    const section = this.element.querySelector('[data-testid="guarantor-section-conditional"]')
    if (!section) {
      return
    }

    const needsGuarantor = this.calculateGuarantorNeeded()

    if (needsGuarantor) {
      section.style.display = ""
      section.classList.remove("hidden")
    } else {
      section.style.display = "none"
      section.classList.add("hidden")
      // A question that is no longer asked should not keep its answer: the
      // household's figures changed, and "Yes" would otherwise be submitted
      // with no guarantor behind it.
      section.querySelectorAll('input[name="guarantor_available"]:checked').forEach(radio => { radio.checked = false })
    }

    this.toggleGuarantorDetails()
  }

  /**
   * Fill the guarantor block of the review step from the details entered on
   * step 3, and show it only when a guarantor has been offered.
   */
  updateGuarantorSummary() {
    if (!this.hasSummaryGuarantorSectionTarget) return

    const offered = this.guarantorDetailsRequired()
    this.summaryGuarantorSectionTarget.classList.toggle("hidden", !offered)
    if (!offered) return

    const value = (target) => (target ? target.value.trim() : "")
    const show = (target, text) => { target.textContent = text || "-" }

    show(this.summaryGuarantorFullNameTarget,
      [value(this.guarantorFirstNameTarget), value(this.guarantorLastNameTarget)].filter(Boolean).join(" "))

    const relationship = this.guarantorRelationshipTarget.selectedOptions[0]
    let relationshipText = ""
    if (relationship && relationship.value === "other") {
      relationshipText = value(this.guarantorRelationshipDetailsInputTarget) || "Other"
    } else if (relationship && relationship.value) {
      relationshipText = relationship.textContent.trim()
    }
    show(this.summaryGuarantorRelationshipTarget, relationshipText)

    const dobValue = value(this.guarantorDobTarget)
    show(this.summaryGuarantorDobTarget, dobValue
      ? new Date(dobValue).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
      : "")

    show(this.summaryGuarantorEmailTarget, value(this.guarantorEmailTarget))
    show(this.summaryGuarantorPhoneTarget, value(this.guarantorPhoneTarget))

    const addressLine2 = this.element.querySelector('input[name="guarantor[address_line_2]"]')
    show(this.summaryGuarantorAddressTarget, [
      value(this.guarantorAddressLine1Target),
      addressLine2 ? addressLine2.value.trim() : "",
      value(this.guarantorTownTarget),
      value(this.guarantorPostcodeTarget).toUpperCase()
    ].filter(Boolean).join(", "))

    show(this.summaryGuarantorOccupationTarget, value(this.guarantorOccupationTarget))

    const income = parseInt(value(this.guarantorIncomeTarget))
    show(this.summaryGuarantorIncomeTarget, isNaN(income) ? "" : `£${income.toLocaleString("en-GB")}`)
  }

  /**
   * Is the guarantor question actually on screen? Always in "always" mode;
   * in "conditional" mode only when the household's figures call for one.
   */
  guarantorQuestionVisible() {
    if (!this.element.querySelector('input[name="guarantor_available"]')) return false
    if (this.guarantorModeValue === "conditional") {
      return this.calculateGuarantorNeeded()
    }
    return true
  }

  // The chosen answer's value, or null while the question stands unanswered.
  guarantorAnswer() {
    const checked = this.element.querySelector('input[name="guarantor_available"]:checked')
    return checked ? checked.value : null
  }

  /**
   * The guarantor's details are required exactly when the applicant has
   * answered "Yes" to a question they can see.
   */
  guarantorDetailsRequired() {
    if (!this.hasGuarantorDetailsTarget) return false
    if (!this.guarantorQuestionVisible()) return false

    return this.guarantorAnswer() === "yes"
  }

  /**
   * Show the guarantor details block when "Yes" is chosen, hide it otherwise.
   * Bound to the guarantor radios, and re-run whenever the conditional
   * question itself appears or disappears.
   */
  toggleGuarantorDetails() {
    if (!this.hasGuarantorDetailsTarget) return

    const block = this.guarantorDetailsTarget
    if (this.guarantorDetailsRequired()) {
      block.style.display = ""
      block.classList.remove("hidden")
    } else {
      block.style.display = "none"
      block.classList.add("hidden")
    }
    this.toggleGuarantorDeclinedWarning()
    this.updateContinueButtonState()
  }

  /**
   * "No" from a household whose income falls short, or that carries adverse
   * credit, is answered with a warning: the application is unlikely to get
   * through without a guarantor. A comfortable household in "always" mode
   * sees no such warning — nothing in their figures calls for one.
   */
  toggleGuarantorDeclinedWarning() {
    if (!this.hasGuarantorDeclinedWarningTarget) return

    const warning = this.guarantorDeclinedWarningTarget
    const declinedWhenNeeded = this.guarantorQuestionVisible() &&
      this.guarantorAnswer() === "no" &&
      this.calculateGuarantorNeeded()

    if (declinedWhenNeeded) {
      warning.style.display = ""
      warning.classList.remove("hidden")
    } else {
      warning.style.display = "none"
      warning.classList.add("hidden")
    }
  }

  /**
   * Everything the agency needs to reference a guarantor and serve a deed on
   * them, as { field: message } so each problem can be shown under its own
   * field. guarantorDetailsErrors() flattens it for the step banner.
   */
  guarantorFieldErrors() {
    const errors = {}
    const value = (target) => (target ? target.value.trim() : "")

    if (!value(this.guarantorFirstNameTarget)) errors.first_name = "Guarantor's first name is required"
    if (!value(this.guarantorLastNameTarget)) errors.last_name = "Guarantor's last name is required"

    const dob = value(this.guarantorDobTarget)
    if (!dob) {
      errors.date_of_birth = "Guarantor's date of birth is required"
    } else if (!this.isAtLeast18(dob)) {
      errors.date_of_birth = "A guarantor must be at least 18"
    }

    const relationship = value(this.guarantorRelationshipTarget)
    if (!relationship) {
      errors.relationship = "Please say who your guarantor is to you"
    } else if (relationship === "other" && !value(this.guarantorRelationshipDetailsInputTarget)) {
      errors.relationship_details = "Please describe your guarantor's relationship to you"
    }

    const phone = value(this.guarantorPhoneTarget)
    if (!phone) {
      errors.phone = "Guarantor's mobile number is required"
    } else if (!this.isValidUKMobile(phone)) {
      errors.phone = "Please enter a valid UK mobile number for your guarantor"
    } else if (this.applicantPhones().includes(this.normalizePhone(phone))) {
      errors.phone = "This is an applicant's number — please enter your guarantor's own mobile number"
    }

    const email = value(this.guarantorEmailTarget)
    if (!email) {
      errors.email = "Guarantor's email address is required"
    } else if (!this.isValidEmail(email)) {
      errors.email = "Please enter a valid email address for your guarantor"
    } else if (this.applicantEmails().includes(email.toLowerCase())) {
      errors.email = "This is an applicant's email — a guarantor cannot be one of the applicants"
    }

    if (!value(this.guarantorAddressLine1Target)) errors.address_line_1 = "Guarantor's postal address is required"
    if (!value(this.guarantorTownTarget)) errors.town = "Guarantor's town or city is required"

    const postcode = value(this.guarantorPostcodeTarget)
    if (!postcode) {
      errors.postcode = "Guarantor's postcode is required"
    } else if (!this.isValidUKPostcode(postcode)) {
      errors.postcode = "Please enter a valid UK postcode for your guarantor"
    }

    if (!value(this.guarantorOccupationTarget)) errors.occupation = "Guarantor's occupation is required"

    const income = parseInt(value(this.guarantorIncomeTarget))
    if (isNaN(income) || income <= 0) errors.annual_income = "Guarantor's approximate annual income is required"

    return errors
  }

  guarantorDetailsErrors() {
    return Object.values(this.guarantorFieldErrors())
  }

  // The field key ("email", "postcode"...) an input in the guarantor block belongs to.
  guarantorFieldKey(input) {
    const match = input && input.name && input.name.match(/^guarantor\[(\w+)\]$/)
    return match ? match[1] : null
  }

  // Typing in the block: refresh the inline messages straight away.
  guarantorFieldChanged() {
    this.renderGuarantorFieldErrors()
  }

  // Leaving a field counts as having visited it, so a blank one may now be
  // told it is required. Until then only a wrong VALUE draws a message, so
  // the block does not open covered in red.
  guarantorFieldLeft(event) {
    const key = this.guarantorFieldKey(event.target)
    if (!key) return
    this.visitedGuarantorFields = this.visitedGuarantorFields || new Set()
    this.visitedGuarantorFields.add(key)
    this.renderGuarantorFieldErrors()
  }

  // Show each problem under its field: visited fields show everything, an
  // unvisited field only complains about what has actually been typed.
  renderGuarantorFieldErrors(showAll = false) {
    if (!this.hasGuarantorDetailsTarget) return

    const errors = this.guarantorFieldErrors()
    const visited = this.visitedGuarantorFields || new Set()

    this.guarantorDetailsTarget.querySelectorAll("[data-guarantor-error]").forEach(slot => {
      const key = slot.dataset.guarantorError
      const message = errors[key]
      const field = this.guarantorDetailsTarget.querySelector(`[name="guarantor[${key}]"]`)
      const hasValue = !!(field && field.value && field.value.trim())
      const show = message && (showAll || visited.has(key) || hasValue)

      slot.textContent = show ? message : ""
      slot.classList.toggle("hidden", !show)
      if (field) field.classList.toggle("border-red-400", !!show)
    })
  }

  // Every applicant's phone as entered so far, normalised like the server's.
  applicantPhones() {
    const phones = []
    if (this.hasLeadPhoneTarget) phones.push(this.normalizePhone(this.leadPhoneTarget.value))
    this.element.querySelectorAll('input[name^="other_adults["][name$="[phone]"]').forEach(input => {
      phones.push(this.normalizePhone(input.value))
    })
    return phones.filter(Boolean)
  }

  // Every applicant's email as entered so far, lower-cased, for the
  // guarantor-is-not-an-applicant check. Mirrors the server's rule.
  applicantEmails() {
    const emails = []
    if (this.hasLeadEmailTarget) emails.push(this.leadEmailTarget.value.trim().toLowerCase())
    this.element.querySelectorAll('input[name^="other_adults["][name$="[email]"]').forEach(input => {
      emails.push(input.value.trim().toLowerCase())
    })
    return emails.filter(Boolean)
  }

  // Eighteen or over on today's date, from a yyyy-mm-dd value.
  isAtLeast18(isoDate) {
    const dob = new Date(isoDate)
    if (isNaN(dob.getTime())) return false
    const cutoff = new Date()
    cutoff.setFullYear(cutoff.getFullYear() - 18)
    return dob <= cutoff
  }

  // A UK mobile: 07 and eleven digits once +44 / spaces are normalised.
  isValidUKMobile(phone) {
    if (!this.isValidUKPhone(phone)) return false
    return this.normalizePhone(phone).startsWith("07")
  }

  // The standard UK postcode shape, inward code and all, case-insensitive.
  isValidUKPostcode(postcode) {
    return /^[A-Z]{1,2}[0-9][A-Z0-9]?\s*[0-9][A-Z]{2}$/i.test(postcode.trim())
  }

  /**
   * Calculate if a guarantor is needed based on:
   * 1. Total household income vs required income threshold
   * 2. Whether any applicant has adverse credit
   */
  calculateGuarantorNeeded() {
    // Get lead applicant income
    const leadIncome = this.hasLeadIncomeTarget ? parseInt(this.leadIncomeTarget.value) || 0 : 0

    // Get other adults income
    const otherAdultIncomeInputs = this.element.querySelectorAll('input[name^="other_adults["][name$="[annual_income]"]')
    let otherAdultsIncome = 0
    otherAdultIncomeInputs.forEach(input => {
      otherAdultsIncome += parseInt(input.value) || 0
    })

    const totalIncome = leadIncome + otherAdultsIncome
    const requiredIncome = this.requiredIncomeValue

    // Check if income is insufficient
    const incomeInsufficient = totalIncome < requiredIncome

    // Check if any applicant has adverse credit
    const leadAdverseCredit = this.element.querySelector('input[name="applicant_1[adverse_credit]"][value="1"]:checked')
    const otherAdverseCredits = this.element.querySelectorAll('input[name^="other_adults["][name$="[adverse_credit]"][value="1"]:checked')
    const hasAdverseCredit = !!leadAdverseCredit || otherAdverseCredits.length > 0

    return incomeInsufficient || hasAdverseCredit
  }
}
